// Geometría del tablero, toda en casillas. Funciones puras, con pruebas.
import type { Area, AreaShape, DiagonalRule, GridOrigin, Token } from '@/api/types';
import { SIZE_CELLS } from './model';

export type Cell = readonly [number, number];
export interface Point {
  x: number;
  y: number;
}

export const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Distancia en casillas entre dos casillas.
 * - `standard` (reglas de 2024): cada diagonal cuenta como 1.
 * - `alternating` (variante de la Guía del DM de 2014): las diagonales alternan 1 y 2 casillas (5-10-5 pies),
 *   así que cada pareja de diagonales cuesta 3.
 */
export function gridDistance(a: Point, b: Point, rule: DiagonalRule = 'standard'): number {
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  if (rule === 'standard') return Math.max(dx, dy);
  const diagonal = Math.min(dx, dy);
  const straight = Math.max(dx, dy) - diagonal;
  return straight + diagonal + Math.floor(diagonal / 2);
}

export const tokenCells = (token: Pick<Token, 'size'>) => SIZE_CELLS[token.size];

export function clampToken(token: Pick<Token, 'size'>, x: number, y: number, cols: number, rows: number): Point {
  const s = tokenCells(token);
  return { x: clamp(Math.round(x), 0, Math.max(0, cols - s)), y: clamp(Math.round(y), 0, Math.max(0, rows - s)) };
}

const rad = (deg: number) => (deg * Math.PI) / 180;
const dir = (angle: number): Point => ({ x: Math.cos(rad(angle)), y: Math.sin(rad(angle)) });

/**
 * Coloca el origen de un área según las reglas de la cuadrícula:
 * círculos, conos y líneas en una esquina de casilla (o en el centro si así se configura);
 * cuadrados con su esquina superior izquierda en una esquina de casilla.
 */
export function snapAreaOrigin(shape: AreaShape, x: number, y: number, origin: GridOrigin): Point {
  if (shape === 'square' || origin === 'corner') return { x: Math.round(x), y: Math.round(y) };
  return { x: Math.round(x - 0.5) + 0.5, y: Math.round(y - 0.5) + 0.5 };
}

/** ¿El punto (centro de casilla) cae dentro del área? */
export function areaContains(area: Area, px: number, py: number): boolean {
  const eps = 1e-6;
  switch (area.shape) {
    case 'circle': {
      const dx = px - area.x;
      const dy = py - area.y;
      return dx * dx + dy * dy <= area.size * area.size + eps;
    }
    case 'square':
      return px >= area.x && px <= area.x + area.size && py >= area.y && py <= area.y + area.size;
    case 'cone':
    case 'line': {
      const d = dir(area.angle);
      const vx = px - area.x;
      const vy = py - area.y;
      const along = vx * d.x + vy * d.y;
      const across = Math.abs(vx * d.y - vy * d.x);
      if (along <= eps || along > area.size + eps) return false;
      // Cono de 2024: su anchura en cada punto es igual a la distancia al origen.
      return area.shape === 'cone' ? across <= along / 2 + eps : across <= area.width / 2 + eps;
    }
  }
}

/** Casillas afectadas: las que tienen su centro dentro del área (y caen en el tablero). */
export function affectedCells(area: Area, cols: number, rows: number): Cell[] {
  const b = areaBounds(area);
  const out: Cell[] = [];
  for (let i = Math.max(0, Math.floor(b.minX)); i < Math.min(cols, Math.ceil(b.maxX)); i++) {
    for (let j = Math.max(0, Math.floor(b.minY)); j < Math.min(rows, Math.ceil(b.maxY)); j++) {
      if (areaContains(area, i + 0.5, j + 0.5)) out.push([i, j]);
    }
  }
  return out;
}

/** Vértices del polígono de un cono o una línea. */
export function areaPolygon(area: Area): Point[] {
  const d = dir(area.angle);
  const n = { x: -d.y, y: d.x };
  const o = { x: area.x, y: area.y };
  const tip = { x: o.x + d.x * area.size, y: o.y + d.y * area.size };
  if (area.shape === 'cone') {
    const half = area.size / 2;
    return [o, { x: tip.x + n.x * half, y: tip.y + n.y * half }, { x: tip.x - n.x * half, y: tip.y - n.y * half }];
  }
  const h = area.width / 2;
  return [
    { x: o.x + n.x * h, y: o.y + n.y * h },
    { x: tip.x + n.x * h, y: tip.y + n.y * h },
    { x: tip.x - n.x * h, y: tip.y - n.y * h },
    { x: o.x - n.x * h, y: o.y - n.y * h },
  ];
}

/** Punta del cono o la línea: ahí va el tirador para girarlos. */
export function areaTip(area: Area): Point {
  const d = dir(area.angle);
  return { x: area.x + d.x * area.size, y: area.y + d.y * area.size };
}

export function areaBounds(area: Area): { minX: number; minY: number; maxX: number; maxY: number } {
  switch (area.shape) {
    case 'circle':
      return { minX: area.x - area.size, minY: area.y - area.size, maxX: area.x + area.size, maxY: area.y + area.size };
    case 'square':
      return { minX: area.x, minY: area.y, maxX: area.x + area.size, maxY: area.y + area.size };
    case 'cone':
    case 'line': {
      const pts = areaPolygon(area);
      return {
        minX: Math.min(...pts.map((p) => p.x)),
        minY: Math.min(...pts.map((p) => p.y)),
        maxX: Math.max(...pts.map((p) => p.x)),
        maxY: Math.max(...pts.map((p) => p.y)),
      };
    }
  }
}

export function areaCenter(area: Area): Point {
  if (area.shape === 'circle') return { x: area.x, y: area.y };
  if (area.shape === 'square') return { x: area.x + area.size / 2, y: area.y + area.size / 2 };
  const pts = areaPolygon(area);
  return { x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length };
}

/** Ángulo en grados [0, 360) del origen al punto, ajustado a pasos de `step` grados. */
export function angleTo(origin: Point, p: Point, step = 15): number {
  const deg = (Math.atan2(p.y - origin.y, p.x - origin.x) * 180) / Math.PI;
  const snapped = step > 0 ? Math.round(deg / step) * step : deg;
  return ((snapped % 360) + 360) % 360;
}

export interface LabelBox {
  id: string;
  /** Posición deseada (centro horizontal, línea base) y alternativas, en orden de preferencia. */
  candidates: Point[];
  width: number;
  height: number;
}

export interface Box {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * Coloca rótulos sin que se pisen entre sí ni con los obstáculos (las peanas):
 * cada uno toma el primer candidato libre que quepa en el tablero. Si ninguno
 * lo está, se queda en el primero.
 */
export function layoutLabels(
  labels: LabelBox[],
  cols: number,
  rows: number,
  obstacles: readonly Box[] = [],
): Map<string, Point> {
  const placed: Box[] = [...obstacles];
  const result = new Map<string, Point>();
  const pad = 0.06;
  for (const label of labels) {
    let chosen: Point | undefined;
    for (const c of label.candidates) {
      const x = clamp(c.x, label.width / 2, cols - label.width / 2);
      const r = {
        x1: x - label.width / 2 - pad,
        x2: x + label.width / 2 + pad,
        y1: c.y - label.height - pad,
        y2: c.y + pad,
      };
      if (r.y1 < 0 || r.y2 > rows) continue;
      if (placed.some((p) => r.x1 < p.x2 && p.x1 < r.x2 && r.y1 < p.y2 && p.y1 < r.y2)) continue;
      chosen = { x, y: c.y };
      placed.push(r);
      break;
    }
    if (!chosen) {
      const first = label.candidates[0] ?? { x: 0, y: 0 };
      chosen = { x: clamp(first.x, label.width / 2, cols - label.width / 2), y: clamp(first.y, label.height, rows) };
    }
    result.set(label.id, chosen);
  }
  return result;
}

/** Candidatos para el rótulo de un área: arriba dentro, abajo dentro, encima, debajo y bajando por dentro. */
export function labelCandidates(area: Area, height: number): Point[] {
  const b = areaBounds(area);
  const c = areaCenter(area);
  const cx = area.shape === 'cone' || area.shape === 'line' ? c.x : (b.minX + b.maxX) / 2;
  const inTop = b.minY + height + 0.15;
  const inBottom = b.maxY - 0.2;
  const list: Point[] = [];
  if (area.shape === 'cone' || area.shape === 'line') list.push({ x: c.x, y: c.y + height / 2 });
  list.push(
    { x: cx, y: inTop },
    { x: cx, y: inBottom },
    { x: cx, y: b.minY - 0.15 },
    { x: cx, y: b.maxY + height + 0.15 },
  );
  for (let y = inTop + height * 1.2; y < inBottom; y += height * 1.2) list.push({ x: cx, y });
  for (let k = 1; k <= 5; k++)
    list.push({ x: cx, y: b.maxY + height + 0.15 + k * height * 1.2 }, { x: cx, y: b.minY - 0.15 - k * height * 1.2 });
  return list;
}

/** Zonas que ocupan las peanas (el disco y su nombre debajo): los rótulos de área las esquivan. */
export function tokenObstacles(tokens: readonly Token[], nameWidth: (name: string) => number): Box[] {
  return tokens.map((tk) => {
    const s = tokenCells(tk);
    const w = Math.max(s, nameWidth(tk.name));
    const cx = tk.x + s / 2;
    return { x1: cx - w / 2, x2: cx + w / 2, y1: tk.y, y2: tk.y + s + 0.4 };
  });
}

/** Casillas de 128 px (lo habitual en los mapas de rol): si el tamaño original encaja, se sugiere la cuadrícula. */
export function suggestGrid(width: number, height: number): { cols: number; rows: number } | null {
  if (width % 128 !== 0 || height % 128 !== 0) return null;
  const cols = width / 128;
  const rows = height / 128;
  return cols >= 5 && rows >= 5 && cols <= 100 && rows <= 100 ? { cols, rows } : null;
}
